import React, { useState, useEffect, useCallback } from 'react';
import { computeScanCoverage, SCAN_COLS, SCAN_COL_LABELS } from '../scanCoverage';
import { QIDS, PLAN_KINDS, loadPlanIndex, activePlan, actualsByQuarter, pctToPlan } from '../msmPlans';

// Narcs'r'us — weekly break-scan compliance. Pulls the same scan %s the Scrib
// Doon (Roll Call) tab computes, for every day/shift in the selected week.
// Grid: rows = SOS / 1st Break / Lunch / 2nd Break, grouped Day then Night;
// columns = Sun..Sat. Value = % of eligible onsite associates with that scan.
// Access-gated to a small allowlist (wired in App.js).

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Sunday of the current Pacific week.
function currentWeekStart() {
  const pac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const sun = new Date(pac);
  sun.setDate(pac.getDate() - pac.getDay());
  sun.setHours(0, 0, 0, 0);
  return ymd(sun);
}
function addDays(ymdStr, n) {
  const d = new Date(ymdStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return ymd(d);
}

export default function NarcsRUs({ api, site }) {
  const [weekStart, setWeekStart] = useState(currentWeekStart());
  const [cov, setCov] = useState({});   // { 'YYYY-MM-DD|day': coverage, ... }
  const [loading, setLoading] = useState(false);
  const [ptp, setPtp] = useState({});   // { 'YYYY-MM-DD|day': { plan, rows: { Q1: pct, ..., shift: pct } } }

  const load = useCallback(() => {
    const dates = DOW.map((_, i) => addDays(weekStart, i));
    const jobs = [];
    dates.forEach(date => {
      ['day', 'night'].forEach(shift => {
        jobs.push(
          Promise.all([
            fetch(`${api}/api/fclm/cache?site=${site}&date=ONSITE_${site}_${date}&shift_type=${shift}`).then(r => r.json()).catch(() => null),
            fetch(`${api}/api/fclm/cache?site=${site}&date=ROSTER_${site}&shift_type=data`).then(r => r.json()).catch(() => null),
            fetch(`${api}/api/fclm/cache?site=${site}&date=ROLLCALL_${date}&shift_type=${shift}`).then(r => r.json()).catch(() => null),
          ]).then(([onsite, roster, rollcall]) => {
            const scans = rollcall && Array.isArray(rollcall.scans) ? rollcall.scans : [];
            const coverage = (onsite && Array.isArray(onsite.onsite))
              ? computeScanCoverage({ onsite, roster, scans }, shift)
              : null;
            return [`${date}|${shift}`, coverage];
          }).catch(() => [`${date}|${shift}`, null])
        );
      });
    });
    // MSM % to plan: active saved plan (Pre-Shift, else Prior-Day) vs FCLM PPR throughput.
    const planJobs = [];
    dates.forEach(date => ['day', 'night'].forEach(shift => {
      planJobs.push(Promise.all([
        loadPlanIndex(api, site, date, shift),
        fetch(`${api}/api/fclm/cache?site=${site}&date=${date}&shift_type=${shift}`).then(r => r.json()).catch(() => null),
      ]).then(([idx, fclm]) => {
        const plan = activePlan(idx);
        const act = actualsByQuarter(fclm, shift);
        const rows = plan ? Object.fromEntries([...QIDS, 'shift'].map(q => [q, pctToPlan(plan.summary, act, q)])) : null;
        return [`${date}|${shift}`, plan ? { plan, rows } : null];
      }).catch(() => [`${date}|${shift}`, null]));
    }));
    setLoading(true);
    Promise.all([Promise.all(jobs), Promise.all(planJobs)]).then(([entries, planEntries]) => {
      setCov(Object.fromEntries(entries));
      setPtp(Object.fromEntries(planEntries));
      setLoading(false);
    });
  }, [api, site, weekStart]);

  useEffect(() => { load(); }, [load]);

  const dates = DOW.map((_, i) => addDays(weekStart, i));
  const pct = (date, shift, col) => {
    const c = cov[`${date}|${shift}`];
    if (!c || !c[col] || c[col].pct == null) return null;
    return c[col].pct;
  };
  const pctColor = (p) => (p == null ? '#bbb' : p >= 0.75 ? '#2e7d32' : p >= 0.5 ? '#f57f17' : '#c62828');

  const th = { border: '1px solid #ccc', padding: '4px 10px', fontSize: '0.72rem', background: '#37474f', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap' };
  const rowLbl = { border: '1px solid #ccc', padding: '4px 10px', fontSize: '0.72rem', textAlign: 'left', fontWeight: 600, background: '#f5f5f5', whiteSpace: 'nowrap' };
  const cell = { border: '1px solid #ccc', padding: '4px 10px', fontSize: '0.75rem', textAlign: 'center' };

  const shiftBlock = (shift) => (
    <>
      <tr>
        <td style={{ ...rowLbl, background: '#eceff1' }}></td>
        {dates.map((d, i) => (
          <th key={d} style={{ ...th, background: shift === 'day' ? '#5c6bc0' : '#37474f' }}>
            {DOW[i]}<br /><span style={{ fontWeight: 400, color: shift === 'day' ? '#ffcc80' : '#90caf9' }}>{shift === 'day' ? 'Day' : 'Night'}</span>
          </th>
        ))}
      </tr>
      {SCAN_COLS.map(col => (
        <tr key={shift + col}>
          <td style={rowLbl}>{SCAN_COL_LABELS[col]}</td>
          {dates.map(d => {
            const p = pct(d, shift, col);
            return <td key={d + col} style={{ ...cell, color: pctColor(p), fontWeight: p != null ? 600 : 400 }}>{p == null ? '—' : `${Math.round(p * 100)}%`}</td>;
          })}
        </tr>
      ))}
    </>
  );

  // ---- MSM % to plan table ----
  const hrsColor = (p) => (p == null ? '#bbb' : p <= 1.0 ? '#2e7d32' : p <= 1.05 ? '#f57f17' : '#c62828'); // over plan hours = bad
  const rateColor = (p) => (p == null ? '#bbb' : p >= 1.0 ? '#2e7d32' : p >= 0.95 ? '#f57f17' : '#c62828'); // under plan rate = bad
  const fmtP = (p) => (p == null ? '—' : `${Math.round(p * 100)}%`);
  const PTP_ROWS = [...QIDS.map(q => [q, q]), ['shift', 'Full Shift']];
  const ptpBlock = (shift) => (
    <>
      <tr>
        <td style={{ ...rowLbl, background: '#eceff1' }}></td>
        {dates.map((d, i) => {
          const e = ptp[`${d}|${shift}`];
          return (
            <th key={d} colSpan={2} style={{ ...th, background: shift === 'day' ? '#5c6bc0' : '#37474f' }} title={e ? `${PLAN_KINDS[e.plan.kind]} plan v${e.plan.n}` : 'No saved MSM plan'}>
              {DOW[i]} <span style={{ fontWeight: 400, color: shift === 'day' ? '#ffcc80' : '#90caf9' }}>{shift === 'day' ? 'Day' : 'Night'}</span>
              <div style={{ fontWeight: 400, fontSize: '0.6rem' }}>{e ? `${e.plan.kind === 'preShift' ? 'PS' : 'PD'} v${e.plan.n}` : 'no plan'}</div>
            </th>);
        })}
      </tr>
      <tr>
        <td style={{ ...rowLbl, background: '#eceff1' }}></td>
        {dates.map(d => (<React.Fragment key={d}><th style={{ ...th, background: '#78909c', fontSize: '0.62rem' }}>Hrs</th><th style={{ ...th, background: '#78909c', fontSize: '0.62rem' }}>Rate</th></React.Fragment>))}
      </tr>
      {PTP_ROWS.map(([q, label]) => (
        <tr key={shift + q}>
          <td style={{ ...rowLbl, fontWeight: q === 'shift' ? 700 : 600 }}>{label}</td>
          {dates.map(d => {
            const r = ptp[`${d}|${shift}`]?.rows?.[q];
            const tip = r ? `Hours ${Math.round(r.actual.hours)} vs ${Math.round(r.plan.hours)} plan · CPLH ${(r.actual.cartons / (r.actual.hours || 1)).toFixed(1)} vs ${(r.plan.cartons / (r.plan.hours || 1)).toFixed(1)}` : '';
            return (<React.Fragment key={d + q}>
              <td title={tip} style={{ ...cell, color: hrsColor(r?.hoursPct), fontWeight: r ? 600 : 400, borderTop: q === 'shift' ? '2px solid #555' : cell.border }}>{fmtP(r?.hoursPct)}</td>
              <td title={tip} style={{ ...cell, color: rateColor(r?.ratePct), fontWeight: r ? 600 : 400, borderTop: q === 'shift' ? '2px solid #555' : cell.border }}>{fmtP(r?.ratePct)}</td>
            </React.Fragment>);
          })}
        </tr>
      ))}
    </>
  );

  return (
    <div className="card">
      <h2 className="section-title">Narcs'r'us — Break Scan %'s ({site})</h2>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px', fontSize: '0.75rem', flexWrap: 'wrap' }}>
        <label>Week of (Sun): <input type="date" value={weekStart} onChange={e => {
          // snap any chosen date back to its Sunday
          const d = new Date(e.target.value + 'T12:00:00');
          d.setDate(d.getDate() - d.getDay());
          setWeekStart(ymd(d));
        }} style={{ fontSize: '0.75rem' }} /></label>
        <button onClick={() => setWeekStart(addDays(weekStart, -7))} style={{ fontSize: '0.7rem', padding: '2px 8px', cursor: 'pointer' }}>← Prev</button>
        <button onClick={() => setWeekStart(currentWeekStart())} style={{ fontSize: '0.7rem', padding: '2px 8px', cursor: 'pointer' }}>This week</button>
        <button onClick={() => setWeekStart(addDays(weekStart, 7))} style={{ fontSize: '0.7rem', padding: '2px 8px', cursor: 'pointer' }}>Next →</button>
        <button onClick={load} style={{ fontSize: '0.7rem', padding: '2px 8px', cursor: 'pointer' }}>↻ Refresh</button>
        {loading && <span style={{ color: '#888' }}>loading…</span>}
      </div>

      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {/* Day block */}
          {shiftBlock('day')}
          {/* spacer */}
          <tr><td colSpan={8} style={{ height: 6, border: 'none' }}></td></tr>
          {/* Night block */}
          {shiftBlock('night')}
        </tbody>
      </table>

      <h3 className="section-title" style={{ fontSize: '0.95rem', marginTop: 18 }}>MSM — Hours &amp; Rate % to Plan</h3>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {ptpBlock('day')}
          <tr><td colSpan={15} style={{ height: 6, border: 'none' }}></td></tr>
          {ptpBlock('night')}
        </tbody>
      </table>
      <p style={{ fontSize: '0.62rem', color: '#888', marginTop: 8 }}>
        Plan = the shift's saved MSM plan (PS = latest Pre-Shift, else PD = latest Prior-Day). Actual = FCLM per hour: hours = PPR throughput (IB + Transfer Out + Support), cartons = FCLM inbound + outbound cartons, so Rate = diluted CPLH.
        Hrs % = actual ÷ plan hours (green ≤100%, amber ≤105%, red &gt;105%). Rate % = actual ÷ plan CPLH (green ≥100%, amber ≥95%, red &lt;95%). Full Shift = all quarters with actuals so far vs their plan. Hover a cell for the numbers.
      </p>

      <p style={{ fontSize: '0.62rem', color: '#888', marginTop: 8 }}>
        % of eligible onsite associates with that break scan (same calc as Roll Call). Green ≥75% · amber ≥50% · red &lt;50% · “—” = no data for that day/shift yet.
        Break 1 counts only associates with an SOS scan/punch; Break 2 only those with a Lunch scan/punch.
      </p>
    </div>
  );
}
